import { sanitizeFilename } from "../sanitizeFilename";
import {
  TEACHER_MANUAL_BUDGET,
  chunkItems,
  coerceText,
  splitByLength,
} from "../adapterHelpers";
import { teacherManualTheme } from "../teacherManualTheme";

function formatLevelLabel(level) {
  if (!level) {
    return "";
  }
  return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
}

function parseMinutes(duration) {
  const match = coerceText(duration).match(/(\d+)/);
  return match ? Number(match[1]) : 0;
}

function totalDurationLabel(steps) {
  const minutes = (steps ?? []).reduce(
    (sum, step) => sum + parseMinutes(step.duration),
    0,
  );
  return minutes > 0 ? `${minutes} mins` : "";
}

function lessonArcLabel(steps) {
  return (steps ?? [])
    .map((step) => coerceText(step.title))
    .filter(Boolean)
    .join(" → ");
}

function insightByVariant(insights, variant) {
  return (insights ?? []).find((insight) => insight.variant === variant) ?? null;
}

function stripQuotes(value) {
  return coerceText(value).replace(/^["']|["']$/g, "").trim();
}

function stepBody(step) {
  if (step.body) {
    return coerceText(step.body);
  }
  if (step.items?.length) {
    return step.items
      .map((item) => `${coerceText(item.label)} ${coerceText(item.text)}`.trim())
      .filter(Boolean)
      .join("\n");
  }
  if (step.highlightBox) {
    return [step.highlightBox.title, step.highlightBox.body]
      .map(coerceText)
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

function toFlowStepCard(step) {
  return {
    title: coerceText(step.title),
    body: splitByLength(stepBody(step), TEACHER_MANUAL_BUDGET.stepBody)[0] ?? "",
    metaLabel: "Duration",
    metaValue: coerceText(step.duration),
  };
}

function misconceptionPairs(insight) {
  if (!insight) {
    return [];
  }
  const pairs = [];
  const quote = stripQuotes(insight.quote);
  const why = insight.sections?.find((section) =>
    section.label.toLowerCase().includes("why"),
  );
  const correction = insight.sections?.find((section) => {
    const label = section.label.toLowerCase();
    return label.includes("correction") || label.includes("strategy");
  });
  const used = new Set([why, correction].filter(Boolean));

  if (quote) {
    pairs.push({
      leftTitle: "Student belief",
      leftBody: quote,
      rightTitle: coerceText(correction?.label) || "Correction strategy",
      rightBody: coerceText(correction?.body),
      leftBullets: why ? [`${why.label}: ${why.body}`] : [],
    });
  }

  for (const section of insight.sections ?? []) {
    if (used.has(section)) {
      continue;
    }
    pairs.push({
      leftTitle: "Misconception",
      leftBody: stripQuotes(section.label),
      rightTitle: "Correction",
      rightBody: coerceText(section.body),
      leftBullets: [],
    });
  }

  return pairs;
}

function withChrome(slide, result) {
  return {
    variant: "light",
    kicker: coerceText(result.curriculumLabel),
    footerTopic: coerceText(result.topicLabel),
    ...slide,
  };
}

function coverSubtitle(result) {
  const body = coerceText(result?.overview?.body);
  if (!body) {
    return coerceText(result?.overview?.heading) || "Teacher guidance for classroom delivery.";
  }
  const sentence = body.match(/^[^.!?]+[.!?]*/);
  return (sentence ? sentence[0] : body).trim();
}

export function teachThisTopicToPptDocument(result) {
  const kicker = coerceText(result?.curriculumLabel);
  const topic = coerceText(result?.topicLabel);
  const slides = [];

  slides.push(
    withChrome(
      {
        kind: "cover",
        title: coerceText(result?.title) || "Teaching Guide",
        subtitle: coverSubtitle(result),
      },
      result,
    ),
  );

  slides.push(
    withChrome(
      {
        kind: "divider",
        title: "Teaching Guide",
        subtitle: "Teacher guidance for classroom delivery.",
      },
      result,
    ),
  );

  const overviewChunks = splitByLength(
    result?.overview?.body,
    TEACHER_MANUAL_BUDGET.overviewBody,
  );
  const keyConcepts = result?.keyConcepts ?? [];
  overviewChunks.forEach((body, index) => {
    slides.push(
      withChrome(
        {
          kind: "overview",
          title: index === 0 ? "Overview" : "Overview (continued)",
          subtitle: coerceText(result?.overview?.heading),
          focal: {
            title: coerceText(result?.overview?.heading) || "The Big Idea",
            body,
          },
          highlight:
            index === 0
              ? {
                  title: "Lesson length",
                  accent: totalDurationLabel(result?.teachingFlow),
                  body: splitByLength(
                    lessonArcLabel(result?.teachingFlow),
                    TEACHER_MANUAL_BUDGET.sidebarBody,
                  )[0],
                }
              : null,
          list:
            index === 0
              ? {
                  title: "Key Concepts",
                  items: keyConcepts.slice(0, 3).map((concept) => ({
                    label: coerceText(concept.title),
                    text: concept.label ? `(${concept.label})` : "",
                  })),
                }
              : null,
        },
        result,
      ),
    );
  });

  chunkItems(keyConcepts, 3).forEach((chunk, index) => {
    slides.push(
      withChrome(
        {
          kind: "steps",
          title: index === 0 ? "Key Concepts" : "Key Concepts (continued)",
          subtitle: "Student-friendly ideas to keep on the board.",
          steps: chunk.map((concept) => ({
            title: coerceText(concept.title),
            body: splitByLength(concept.body, TEACHER_MANUAL_BUDGET.stepBody)[0] ?? "",
            metaLabel: "Label",
            metaValue: coerceText(concept.label),
          })),
        },
        result,
      ),
    );
  });

  const flowSteps = result?.teachingFlow ?? [];
  const itemStep = flowSteps.find((step) => step.items?.length);
  const narrativeSteps = flowSteps.filter((step) => step !== itemStep);

  chunkItems(narrativeSteps, 3).forEach((chunk, index) => {
    slides.push(
      withChrome(
        {
          kind: "steps",
          title: index === 0 ? "Teaching Flow" : "Teaching Flow (continued)",
          subtitle: "What happens in class, in order.",
          steps: chunk.map(toFlowStepCard),
        },
        result,
      ),
    );
  });

  if (itemStep) {
    chunkItems(itemStep.items, 3).forEach((chunk, index) => {
      slides.push(
        withChrome(
          {
            kind: "steps",
            title:
              index === 0
                ? coerceText(itemStep.title) || "Direct Instruction"
                : `${coerceText(itemStep.title)} (continued)`,
            subtitle: coerceText(itemStep.duration)
              ? `${itemStep.duration} of focused teaching`
              : "Break the explanation into teachable moves.",
            steps: chunk.map((item) => ({
              title: coerceText(item.label).replace(/:$/, ""),
              body: splitByLength(item.text, TEACHER_MANUAL_BUDGET.stepBody)[0] ?? "",
              metaLabel: "Duration",
              metaValue: coerceText(itemStep.duration),
            })),
          },
          result,
        ),
      );
    });
  }

  const misconception = insightByVariant(result?.sideInsights, "misconception");
  misconceptionPairs(misconception).forEach((pair, index) => {
    const rightChunks = splitByLength(
      pair.rightBody,
      TEACHER_MANUAL_BUDGET.compareBody,
    );
    rightChunks.forEach((rightBody, chunkIndex) => {
      slides.push(
        withChrome(
          {
            kind: "compare",
            title:
              index === 0 && chunkIndex === 0
                ? coerceText(misconception.title) || "Misconceptions"
                : "Misconceptions (continued)",
            subtitle: "Name the belief, then teach the correction.",
            left: {
              badge: "Misconception",
              title: pair.leftTitle,
              body: splitByLength(pair.leftBody, TEACHER_MANUAL_BUDGET.compareBody)[0],
              bullets: chunkIndex === 0 ? pair.leftBullets.slice(0, 2) : [],
            },
            right: {
              badge: "Correction",
              title: pair.rightTitle,
              body: rightBody,
            },
          },
          result,
        ),
      );
    });
  });

  const realWorld = insightByVariant(result?.sideInsights, "realWorld");
  const realWorldSections = realWorld?.sections ?? [];
  if (realWorldSections.length === 2) {
    slides.push(
      withChrome(
        {
          kind: "compare",
          title: coerceText(realWorld.title) || "Real World",
          subtitle: "Two classroom-ready connections.",
          left: {
            badge: "Connection",
            title: coerceText(realWorldSections[0].label),
            body: splitByLength(
              realWorldSections[0].body,
              TEACHER_MANUAL_BUDGET.compareBody,
            )[0],
          },
          right: {
            badge: "Connection",
            title: coerceText(realWorldSections[1].label),
            body: splitByLength(
              realWorldSections[1].body,
              TEACHER_MANUAL_BUDGET.compareBody,
            )[0],
          },
        },
        result,
      ),
    );
  } else {
    chunkItems(realWorldSections, 3).forEach((chunk, index) => {
      slides.push(
        withChrome(
          {
            kind: "steps",
            title:
              index === 0
                ? coerceText(realWorld?.title) || "Real World"
                : "Real World (continued)",
            subtitle: "Everyday links students can recognize.",
            steps: chunk.map((section) => ({
              title: coerceText(section.label),
              body: splitByLength(section.body, TEACHER_MANUAL_BUDGET.stepBody)[0] ?? "",
            })),
          },
          result,
        ),
      );
    });
  }

  chunkItems(result?.quickCheck ?? [], 3).forEach((chunk, index) => {
    slides.push(
      withChrome(
        {
          kind: "steps",
          title: index === 0 ? "Quick Check" : "Quick Check (continued)",
          subtitle: "Ask these before the period ends.",
          steps: chunk.map((column) => ({
            title: formatLevelLabel(column.level),
            body: splitByLength(column.question, TEACHER_MANUAL_BUDGET.stepBody)[0] ?? "",
            metaLabel: "Level",
            metaValue: coerceText(column.level).toUpperCase(),
          })),
        },
        result,
      ),
    );
  });

  if (result?.visualizing?.title || result?.visualizing?.subtitle) {
    const bodyChunks = splitByLength(
      result.visualizing.subtitle,
      TEACHER_MANUAL_BUDGET.mediaBody,
    );
    bodyChunks.forEach((body, index) => {
      slides.push(
        withChrome(
          {
            kind: "media",
            title:
              index === 0
                ? coerceText(result.visualizing.title)
                : `${coerceText(result.visualizing.title)} (continued)`,
            subtitle: "Use a board drawing or diagram here.",
            imageLabel: topic || "Visual",
            heading: coerceText(result.visualizing.title),
            body,
          },
          result,
        ),
      );
    });
  }

  return {
    filename: sanitizeFilename(result?.title, "teaching-guide"),
    title: coerceText(result?.title) || "Teaching Guide",
    subject: kicker,
    author: "Tema Lesson Planner",
    template: "teacher-manual",
    theme: teacherManualTheme,
    slides,
  };
}
